import type { DealInsightsSpendApi } from '@openx/types/dealInsights';
import { addDays, addMonths, addWeeks, format, isValid, parseISO, startOfMonth, subDays } from 'date-fns';

export type DateResolution = 'daily' | 'weekly' | 'monthly';

export interface FillDataGapsParams {
  rawValues: DealInsightsSpendApi[];
  startDate: Date;
  endDate: Date;
  dateResolution: DateResolution;
}

/**
 * Fills gaps in spend data with zero values based on the selected date resolution.
 *
 * @param params - Configuration object containing raw data, date range, and resolution
 * @returns Array of spend data with gaps filled
 */
export const fillSpendDataGaps = ({
  rawValues,
  startDate,
  endDate,
  dateResolution,
}: FillDataGapsParams): DealInsightsSpendApi[] => {
  if (!isValid(startDate) || !isValid(endDate) || rawValues.length === 0) {
    return rawValues;
  }

  const dataMap = new Map(rawValues.map(item => [item.date, item]));

  const firstApiDate = rawValues[0]?.date;
  if (!firstApiDate) {
    return rawValues;
  }

  const firstApiDateObj = parseISO(firstApiDate);
  const dates: DealInsightsSpendApi[] = [];

  // Always include the first API data point
  dates.push(rawValues[0]);

  let currentDate: Date;

  switch (dateResolution) {
    case 'daily': {
      const yesterday = subDays(new Date(), 1);
      const isEndDateYesterday = format(endDate, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd');

      currentDate = addDays(firstApiDateObj, 1);
      while (currentDate < endDate) {
        const dateString = format(currentDate, 'yyyy-MM-dd');
        dates.push(
          dataMap.get(dateString) ?? {
            date: dateString,
            spend: 0,
            ecpm: 0,
            viewability: 0,
            vcr: 0,
            impressions: 0,
          }
        );
        currentDate = addDays(currentDate, 1);
      }

      // Handle the last day based on whether endDate is yesterday
      const lastDateString = format(endDate, 'yyyy-MM-dd');
      const firstApiDateString = format(firstApiDateObj, 'yyyy-MM-dd');
      const isEndDateSameAsFirstApiDate = lastDateString === firstApiDateString;
      const lastDayData = dataMap.get(lastDateString);

      if (isEndDateSameAsFirstApiDate) {
        break;
      }

      if (isEndDateYesterday) {
        // If endDate is yesterday, only add it if it exists in API data
        if (lastDayData) {
          dates.push(lastDayData);
        }
        break;
      }

      // If endDate is not yesterday, fill with zero if missing from API
      dates.push(
        lastDayData ?? {
          date: lastDateString,
          spend: 0,
          ecpm: 0,
          viewability: 0,
          vcr: 0,
          impressions: 0,
        }
      );
      break;
    }

    case 'weekly': {
      const daysUntilNextMonday = (8 - firstApiDateObj.getDay()) % 7 || 7;
      currentDate = addDays(firstApiDateObj, daysUntilNextMonday);
      while (currentDate <= endDate) {
        const dateString = format(currentDate, 'yyyy-MM-dd');
        dates.push(
          dataMap.get(dateString) ?? {
            date: dateString,
            spend: 0,
            ecpm: 0,
            viewability: 0,
            vcr: 0,
            impressions: 0,
          }
        );
        currentDate = addWeeks(currentDate, 1);
      }
      break;
    }
    case 'monthly':
      currentDate = addMonths(startOfMonth(firstApiDateObj), 1);
      while (currentDate <= endDate) {
        const dateString = format(currentDate, 'yyyy-MM-dd');
        dates.push(
          dataMap.get(dateString) ?? {
            date: dateString,
            spend: 0,
            ecpm: 0,
            viewability: 0,
            vcr: 0,
            impressions: 0,
          }
        );
        currentDate = addMonths(currentDate, 1);
      }
      break;
    default:
      // Fallback to daily
      currentDate = addDays(firstApiDateObj, 1);
      while (currentDate <= endDate) {
        const dateString = format(currentDate, 'yyyy-MM-dd');
        dates.push(
          dataMap.get(dateString) ?? {
            date: dateString,
            spend: 0,
            ecpm: 0,
            viewability: 0,
            vcr: 0,
            impressions: 0,
          }
        );
        currentDate = addDays(currentDate, 1);
      }
  }

  return dates;
};
